import React from 'react';
import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import { CenteredFlexBox, FullSizeColumn } from '../../../Components/FlexBox';
import { culture } from '../../../Helpers/dates';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '../../../Components/Chip';
import { ACTIVITY_TYPES_ICONS } from '../../../Utils/constants/activityTypes';
import {
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
} from '../../../Atoms';

const CustomerCard = function CustomerCard({
  id,
  customerName = '',
  lastActivityCompletedOn = '',
  lastActivityType = '',
  lastActivityCompletedBy = '',
}) {
  const intl = useIntl();
  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const Icon = ACTIVITY_TYPES_ICONS[lastActivityType] || null;

  return (
    <Accordion
      slotProps={{
        root: {
          variant: 'outlined',
        },
      }}
      sx={{
        borderRadius: '18px',
        '&:before': {
          display: 'none', // Remove default divider line
        },
        '&:first-of-type': {
          borderRadius: '18px', // Top corners only when first
        },
        '&:last-of-type': {
          borderRadius: '18px', // Bottom corners only when last
        },
        '&.Mui-expanded': {
          margin: '4px 0px', // Override the default 16px margin when expanded
        },
      }}
    >
      <AccordionSummary
        id={`customer-card-${customerName}`}
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderRadius: '18px',
          '&.MuiAccordionSummary-root': {
            minHeight: 'unset',
          },
          '&.Mui-expanded': {
            margin: 0,
            minHeight: 'unset',
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '8px 0',
          },
        }}
      >
        <Typography
          title={capitalize(customerName)}
          onClick={() => {
            setSelectedCustomerId(id);
            setAdditionalCustomerData({
              customerName: customerName,
              sectionId: 'CUSTOMERS',
              outcomesFilter: 'CUSTOMERS',
            });
          }}
          sx={{
            fontFamily: 'Roboto',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {capitalize(customerName)}
        </Typography>
      </AccordionSummary>
      <Divider variant="middle" />
      <AccordionDetails sx={{ borderRadius: '0 0 18px 18px' }}>
        {!lastActivityType && (
          <Typography variant="body1">
            {intl.messages.NO_RECENT_ACTIVITY}
          </Typography>
        )}
        {lastActivityType && (
          <FullSizeColumn gap="4px">
            <Typography variant="body1">
              {intl.messages.LAST_ACTIVITY}
            </Typography>
            <Typography variant="body2">
              {lastActivityCompletedOn &&
                new Date(lastActivityCompletedOn)?.toLocaleDateString(culture, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
            </Typography>
            <CenteredFlexBox gap="8px">
              {lastActivityCompletedBy && (
                <Chip
                  id={`${customerName}-lastActivityCompletedBy`}
                  size="small"
                  label={lastActivityCompletedBy}
                />
              )}
              {Icon && (
                <Avatar
                  sx={{
                    marginLeft: 'auto',
                    bgcolor: 'primary.main',
                    color: '#fff',
                    width: 34,
                    height: 34,
                  }}
                >
                  <Icon />
                </Avatar>
              )}
            </CenteredFlexBox>
          </FullSizeColumn>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomerCard;
